import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Check, MessageCircle, ChevronDown, MapPin, Sparkles, Tag, Phone, ArrowUpRight, Shield, Plus,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";
import { SITE_URL, buildLocalServiceLd, buildBreadcrumbLd, buildFaqLd } from "@/lib/seo";
import { getLocalPage, indexableServicesForCity } from "@/lib/localPages";
import { useSeoT } from "@/lib/seoPagesI18n";
import { SERVICE_LOCAL_I18N, CITY_LOCAL_I18N } from "@/lib/localContentI18n";

const TARIF_SLUGS = { demenagement: "demenagement", debarras: "debarras", nettoyage: "nettoyage-fin-de-bail" };

export default function ServiceCityPage() {
  const { slug, ville } = useParams();
  const navigate = useNavigate();
  const { lang, t: st, serviceName } = useSeoT();
  const [openIdx, setOpenIdx] = useState(0);
  const page = getLocalPage(slug, ville);

  if (!page) return <NotFound />;

  const sc = st.serviceCity;
  const svcName = serviceName[page.serviceSlug] || page.serviceName;
  const svcLoc = lang !== "fr" ? (SERVICE_LOCAL_I18N[page.serviceKey]?.[lang] || {}) : {};
  const cityLoc = lang !== "fr" ? (CITY_LOCAL_I18N[page.citySlug]?.[lang] || {}) : {};
  const intro = svcLoc.intro || page.intro;
  const tagline = svcLoc.tagline || page.tagline;
  const advantages = svcLoc.advantages || page.advantages;
  const options = svcLoc.options || page.options;
  const useCases = svcLoc.useCases || page.useCases;
  const localContext = cityLoc.localContext || page.localContext;
  const faq = cityLoc.faq || page.faq;
  const metaTitle = `${svcName} ${st.connect} ${page.cityName} | Mopper`;
  const metaDesc = `${svcName} ${st.connect} ${page.cityName} (${page.canton}) : ${tagline} ${lang === "de" ? "Klares, kostenloses Angebot." : lang === "en" ? "Clear, free quote." : "Devis clair et gratuit."}`.slice(0, 320);

  const bc = [
    { name: st.bc.home, path: "/" },
    { name: st.bc.services, path: "/services" },
    { name: svcName, path: `/services/${page.serviceSlug}` },
    { name: page.cityName, path: page.path },
  ];
  const jsonLd = [
    buildLocalServiceLd({
      serviceName: svcName, cityName: page.cityName, canton: page.canton,
      description: metaDesc, url: page.url,
    }),
    buildBreadcrumbLd(toLdItems(bc)),
  ];
  if (faq && faq.length) jsonLd.push(buildFaqLd(faq));

  const whatsappHref = `https://wa.me/41782321850?text=${encodeURIComponent(
    lang === "de"
      ? `Hallo Mopper, ich möchte ein Angebot für ${page.serviceName} in ${page.cityName}`
      : lang === "en"
      ? `Hello Mopper, I would like a quote for ${page.serviceName} in ${page.cityName}`
      : `Bonjour Mopper, je souhaite un devis ${page.serviceName} à ${page.cityName}`
  )}`;
  const siblings = indexableServicesForCity(page.citySlug).filter((p) => p.serviceSlug !== page.serviceSlug);
  const tarifSlug = TARIF_SLUGS[page.serviceSlug];

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid={`service-city-${page.serviceSlug}-${page.citySlug}`}>
      <Seo title={metaTitle} description={metaDesc} path={page.path} noindex={!page.indexable} jsonLd={jsonLd} />
      <Header />

      {/* HERO */}
      <section className="relative bg-[#2D5BFF] hero-noise overflow-hidden pt-28 md:pt-36 pb-14 md:pb-20 px-5 sm:px-8 md:px-10 lg:px-14 text-white">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1280px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#FFD84D] font-bold mb-5">
            <MapPin size={12} /> {svcName} · {page.cityName}
          </span>
          <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {svcName} {st.connect} {page.cityName}, <span className="text-[#FFD84D]">{sc.heroSuffix}</span>.
          </h1>
          <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl">{tagline}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button onClick={() => navigate(`/devis?service=${page.serviceKey}`)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm md:text-base transition-all shadow-[0_15px_30px_-10px_rgba(255,216,77,0.55)]" data-testid="sc-cta-quote">
              {sc.ctaQuote} <ArrowRight size={18} />
            </button>
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white px-7 py-4 font-semibold text-sm md:text-base transition-all" data-testid="sc-cta-whatsapp">
              <MessageCircle size={18} /> {sc.ctaWhatsapp}
            </a>
          </div>
        </div>
      </section>

      {/* RÉPONSE DIRECTE */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10" style={{ maxWidth: "1280px" }}>
          <div>
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {sc.answerTitle(svcName, page.cityName)}
            </h2>
            <p className="mt-5 text-[#4B5563] text-base md:text-lg leading-relaxed">{intro}</p>
            <p className="mt-4 text-[#4B5563] text-base md:text-lg leading-relaxed">{localContext}</p>
          </div>
          <aside className="bg-[#F7F7F8] rounded-3xl p-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{sc.whatIncludes}</div>
            <ul className="mt-4 flex flex-col gap-2.5">
              {advantages.slice(0, 6).map((a, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px]"><span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#2D5BFF] text-white shrink-0"><Check size={11} strokeWidth={3} /></span><span>{a}</span></li>
              ))}
            </ul>
            {tarifSlug && (
              <Link to={`/tarifs/${tarifSlug}`} className="mt-6 inline-flex items-center gap-2 text-[#2D5BFF] font-bold text-sm hover:gap-3 transition-all" data-testid="sc-tarif-link">
                <Tag size={15} /> {sc.seeTarifs(svcName)} <ArrowRight size={14} />
              </Link>
            )}
          </aside>
        </div>
      </section>

      {/* PROCESS — comment ça se passe */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#0A0F2C] text-white">
        <div className="mx-auto" style={{ maxWidth: "1280px" }}>
          <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{sc.processTitle}</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {sc.process.map((s, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid={`sc-process-${i}`}>
                <div className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#FFD84D] text-[#0A0F2C] font-extrabold text-sm">{i + 1}</div>
                <h3 className="mt-4 font-extrabold text-base" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.t}</h3>
                <p className="mt-2 text-white/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAS D'USAGE */}
      {useCases.length > 0 && (
        <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
          <div className="mx-auto" style={{ maxWidth: "1280px" }}>
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {sc.usecasesTitle(page.cityName)}
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {useCases.slice(0, 6).map((c, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100" data-testid={`sc-usecase-${i}`}>
                  <div className="w-9 h-9 rounded-xl bg-[#FFF8DC] text-[#A88A00] inline-flex items-center justify-center"><Sparkles size={16} /></div>
                  <h3 className="mt-4 font-extrabold text-base text-[#0A0F2C] leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OPTIONS + GARANTIES */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8" style={{ maxWidth: "1280px" }}>
          {options && options.length > 0 && (
            <div className="bg-[#F7F7F8] rounded-3xl p-8" data-testid="sc-options">
              <h2 className="font-extrabold text-xl md:text-2xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{sc.optionsTitle}</h2>
              <ul className="mt-6 flex flex-col gap-3">
                {options.map((o, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[15px]" data-testid={`sc-option-${i}`}><span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#EAF0FF] text-[#2D5BFF] shrink-0"><Plus size={12} strokeWidth={3} /></span><span>{o}</span></li>
                ))}
              </ul>
            </div>
          )}
          <div className="bg-[#EAF0FF] rounded-3xl p-8" data-testid="sc-proofs">
            <div className="w-10 h-10 rounded-xl bg-[#2D5BFF] text-white inline-flex items-center justify-center"><Shield size={19} /></div>
            <h2 className="mt-4 font-extrabold text-xl md:text-2xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{sc.proofsTitle}</h2>
            <ul className="mt-6 flex flex-col gap-3">
              {sc.proofs.map((p, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px]" data-testid={`sc-proof-${i}`}><span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#2D5BFF] text-white shrink-0"><Check size={11} strokeWidth={3} /></span><span>{p}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* QUARTIERS / ZONES */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8" style={{ maxWidth: "1280px" }}>
          <div className="bg-[#0A0F2C] text-white rounded-3xl p-8">
            <h2 className="font-bold text-base uppercase tracking-[0.18em] text-[#FFD84D]">{sc.neighborhoodsTitle(page.cityName)}</h2>
            <ul className="mt-6 grid grid-cols-2 gap-y-2.5 gap-x-4">
              {page.neighborhoods.map((n) => (
                <li key={n} className="flex items-center gap-2 text-[15px]"><Check size={14} className="text-[#FFD84D] shrink-0" /> {n}</li>
              ))}
            </ul>
            {page.nearbyCommunes.length > 0 && (
              <p className="mt-6 text-white/85 text-[15px] leading-relaxed">{sc.nearbyLabel} : {page.nearbyCommunes.join(", ")}.</p>
            )}
          </div>
          <div className="bg-white rounded-3xl p-8 flex flex-col justify-center border border-gray-100">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{sc.seeAlso}</div>
            <div className="mt-4 flex flex-col gap-3">
              <Link to={`/villes/${page.citySlug}`} className="inline-flex items-center gap-2 font-semibold hover:text-[#2D5BFF]" data-testid="sc-city-hub-link">
                {sc.allServicesIn(page.cityName)} <ArrowUpRight size={16} />
              </Link>
              <Link to={`/services/${page.serviceSlug}`} className="inline-flex items-center gap-2 font-semibold hover:text-[#2D5BFF]" data-testid="sc-service-link">
                {sc.ourService(svcName)} <ArrowUpRight size={16} />
              </Link>
              <Link to={`/zones/${page.cantonSlug}`} className="inline-flex items-center gap-2 font-semibold hover:text-[#2D5BFF]" data-testid="sc-canton-link">
                {sc.cantonCoverage(page.canton)} <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faq.length > 0 && (
        <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
          <div className="mx-auto" style={{ maxWidth: "900px" }}>
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight text-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {sc.faqTitle(svcName, page.cityName)}
            </h2>
            <div className="mt-8 flex flex-col gap-3">
              {faq.map((it, i) => {
                const open = openIdx === i;
                return (
                  <div key={i} className="bg-[#F7F7F8] rounded-2xl overflow-hidden" data-testid={`sc-faq-${i}`}>
                    <button className="w-full text-left p-5 md:p-6 flex items-center justify-between gap-4" onClick={() => setOpenIdx(open ? -1 : i)} aria-expanded={open}>
                      <span className="font-bold text-[#0A0F2C] text-base md:text-lg">{it.q}</span>
                      <ChevronDown className={`shrink-0 text-[#2D5BFF] transition-transform ${open ? "rotate-180" : ""}`} size={20} />
                    </button>
                    {open && <div className="px-5 md:px-6 pb-5 md:pb-6 text-[#4B5563] leading-relaxed">{it.a}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* MAILLAGE : autres services dans cette ville */}
      {siblings.length > 0 && (
        <section className="py-12 md:py-16 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
          <div className="mx-auto" style={{ maxWidth: "1280px" }}>
            <div className="border-t border-gray-200 pt-10">
              <h2 className="font-extrabold text-xl md:text-2xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {sc.otherServices(page.cityName)}
              </h2>
              <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {siblings.map((s) => (
                  <Link key={s.serviceSlug} to={s.path} className="group bg-white hover:bg-[#EAF0FF] rounded-2xl p-5 transition-colors border border-gray-100" data-testid={`sc-sibling-${s.serviceSlug}`}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#9CA3AF]">{page.cityName}</div>
                    <div className="mt-1 font-extrabold text-[#0A0F2C] text-base group-hover:text-[#2D5BFF] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{serviceName[s.serviceSlug] || s.serviceName}</div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#2D5BFF]">{sc.discover} <ArrowRight size={12} /></div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="relative py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#0A0F2C] text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(45,91,255,0.35), transparent 55%)" }} />
        <div className="relative mx-auto text-center" style={{ maxWidth: "900px" }}>
          <h2 className="font-extrabold text-3xl md:text-5xl tracking-tighter leading-[0.98]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {svcName} {st.connect} <span className="text-[#FFD84D]">{page.cityName}</span> ?
          </h2>
          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl mx-auto">{sc.ctaText}</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate(`/devis?service=${page.serviceKey}`)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm md:text-base transition-all">
              {sc.askQuote} <ArrowRight size={18} />
            </button>
            <a href="tel:+41782321850" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white px-7 py-4 font-semibold text-sm md:text-base transition-all">
              <Phone size={18} /> {sc.callback}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
